import React from "react";
import { IconButton, Card, Typography, Box } from "@material-ui/core";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import { makeStyles } from '@material-ui/core/styles';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import './KeyFigures.css'

// Function to format date from yyyy/mm/dd to dd/mm/yyyy
const formatDate = (dateString) => {
    if (!dateString) return "";
    
    // Check if the date is in yyyy/mm/dd format
    const dateParts = dateString.split('/');
    if (dateParts.length === 3) {
        // Rearrange the parts to dd/mm/yyyy
        return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
    }
    
    // Check if the date is in yyyy-mm-dd format
    const datePartsDash = dateString.split('-');
    if (datePartsDash.length === 3) {
        // Rearrange the parts to dd/mm/yyyy
        return `${datePartsDash[2]}/${datePartsDash[1]}/${datePartsDash[0]}`;
    }
    
    // Return original if format is not recognized
    return dateString;
};

const useStyles = makeStyles(theme => ({
    cardHeader: {
        position: "absolute",
        right: 0,
    },
    card: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: "0n",
        backgroundColor: props => `${theme.palette.primary.main}${props.opacity}`,
        position: "relative",
        transition: "all .5s ease",
        WebkitTransition: "all .5s ease",
        MozTransition: "all .5s ease",
        minWidth: '220px',
    },
    clicked: {
        flexGrow: 2,
        flexShrink: 0,
        flexBasis: "0n",
        backgroundColor: theme.palette.secondary.main,
        position: "relative",
        transition: "all .5s ease",
        WebkitTransition: "all .5s ease",
        MozTransition: "all .5s ease",
        minWidth: '220px',
    },
    cardContent: {
        padding: theme.spacing(1, 2, 0, 2),  
        [theme.breakpoints.down("lg")]: {
            paddingRight: "10px",
            paddingTop: "10px", 
            paddingBottom: "0",  
        }
    },
    cardIconButton: {
        [theme.breakpoints.down("lg")]: {
            paddingRight: "0",
            paddingTop: "1px"
        }
    },
    attendeeCardIconButton: {
        padding: "4px",
        marginTop: "-16px",
        marginRight: "-4px",
        [theme.breakpoints.down("lg")]: {
            padding: "2px",
            marginTop: "-16px",
            marginRight: "-6px",
        },
        [theme.breakpoints.down("sm")]: {
            padding: "1px",
            marginTop: "-16px",
            marginRight: "-8px",
        },
 
        '@media (max-width: 1440px) and (min-width: 1280px)': {
            padding: "3px",
            marginTop: "-18px", 
            marginRight: "12px",  
        }
    },
 
    mostRecentInfoButton: {
        padding: "4px",
        marginRight: "15px", 
        [theme.breakpoints.down("lg")]: {
            padding: "2px",
            marginRight: "15px",  
        },
        [theme.breakpoints.down("sm")]: {
            padding: "1px",
            marginRight: "15px",  
        }
    },
    inspectionExitButton: {
        padding: "4px",
        marginTop: "-8px",
        marginRight: "-4px",
        [theme.breakpoints.down("lg")]: {
            padding: "2px",
            marginTop: "-8px",
            marginRight: "-4px",
        },
        [theme.breakpoints.down("sm")]: {
            padding: "1px",
            marginTop: "-8px",
            marginRight: "-4px",
        }
    },
    gridContainer: {
        width: '100%',
        marginBottom: 10,
        paddingtTop: 2,
        height: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        
        '@media (max-width: 1440px) and (min-width: 1280px)': {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '100px',
            overflowY: 'auto',
            marginBottom: '-25px',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
                width: '6px',
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgb(59, 59, 59)',
                borderRadius: '10px',
            }
        }
    },
    fullSection: {
        position: 'relative',
        height: '100%',
        width: '100%',
        marginBottom: -10,
        paddingTop: 10,
        display: 'flex',
        flexDirection: 'column'
    },
    dataLabel: {
        color: 'white',
        fontSize: '0.7rem', 
        opacity: 0.9,
        marginBottom: theme.spacing(0.25),
        '@media (max-width: 1440px) and (min-width: 1280px)': {
         marginTop: '10px'
        }
    },
    sectionHeader: {
        color: 'white',
        fontSize: '1rem',
        fontWeight: 500,
        paddingTop: theme.spacing(0.5), 
        marginBottom: theme.spacing(2)  
    },
    sectionContent: {
        marginTop: theme.spacing(0.5), 
        marginBottom: 0, 
        padding: theme.spacing(0, 0), 
    },
    sectionHeaderContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        paddingRight: "2px",
        minHeight: '30px', 
    },
    dateRow: {
        display: 'flex',
        color: 'white',  // Ensuring date row text is white
        padding: theme.spacing(0.25, 0), 
    },
    dateColumn: {
        width: '65%',
        textAlign: 'left',
        color: 'white'  // Ensuring date column text is white
    },
    attendeesColumn: {
        width: '35%',
        textAlign: 'left',
        paddingLeft: theme.spacing(2),
        color: 'white'  // Ensuring attendees column text is white
    },
    columnHeaders: {
        display: 'flex',
        color: 'white',
        fontWeight: 'bold',
        borderBottom: '1px solid rgba(255,255,255,0.3)',
        marginBottom: theme.spacing(0.25), 
        width: '100%'
    },
    columnHeaderDate: {
        width: '65%',
        textAlign: 'left',
        color: 'white'  // Ensuring column header date text is white
    },
    columnHeaderAttendees: {
        width: '35%',
        textAlign: 'left',
        paddingLeft: theme.spacing(2),
        color: 'white'  // Ensuring column header attendees text is white
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        marginBottom: theme.spacing(0),
        paddingRight: "2px"
    },
    scrollableArea: {
        maxHeight: '90px', 
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: theme.spacing(1.5),
        marginRight: theme.spacing(1),
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgb(59, 59, 59)',
            borderRadius: '10px',
        }
    },
    nonScrollableArea: {
        width: '100%',
    },
    dataValue: {
        color: 'white',
        marginBottom: 0,
        fontSize: '0.9rem',
        lineHeight: 1.2, 
    }
}));

export default function KeyCard(props) {
    const classes = useStyles(props);
    const infoBox = {
        "Page Visits": "Where available, the combined total Visits to your property listing page, across the relevant portals and websites.",
        "Email Enquiries": "Where available, the combined total Email Enquiries for your property listing, across the relevant portals and websites.",
        "Saves": "Where available, the combined total Saves (favouriting and shortlisting) of your property listing, across the relevant portals and websites.",
        "Shares": "Where available, the combined total Shares (social and email) of your property listing, across the relevant portals and websites.",
        "Inspections - Total": "Where available, the total number of attendees across all property inspections.",
    }
    
    const handleClick = (section) => {
        props.onClick(section);
    }

    const isAttendeeCard = props.name === "Inspections - Total";
    
    const isAttendeeCardOpen = props.open === "Inspections - Total" || props.open === "Inspections - Most Recent";
    
    // Modified to show formatted date and start time
    const getMostRecentInspection = () => {
        if (props.recentInspectionData && props.recentInspectionData.length > 0) {
            const mostRecent = props.recentInspectionData[0];
            return `${formatDate(mostRecent.date)} ${mostRecent.start_time || ''}`;
        }
        return "No inspections";
    };

    const renderAttendeeCard = () => {
        if (props.open === "Inspections - Total") {
    
            return (
                <Box className={classes.fullSection}>
                    <Box className={classes.sectionHeaderContainer}>
                        <Typography variant="body1" className={classes.sectionHeader}>Inspections - Total</Typography>
                        <IconButton onClick={() => handleClick("Inspections - Total")} className={classes.inspectionExitButton}>
                            <ClearOutlinedIcon fontSize="small" style={{fill:"white"}}/>
                        </IconButton>
                    </Box>
                    <Typography variant="subtitle1" style={{color:"white", paddingRight: "10px"}}>
                        {infoBox["Inspections - Total"]}
                    </Typography>
                </Box>
            );
        } else if (props.open === "Inspections - Most Recent") {

            const needsScrolling = props.recentInspectionData && props.recentInspectionData.length > 3;
            
            return (
                <Box className={classes.fullSection}>
                    <Box className={classes.headerContainer}>
                        <Box className={classes.columnHeaders} style={{borderBottom: 'none', marginBottom: 0}}>
                            <Typography variant="body2" className={classes.columnHeaderDate}>Most Recent</Typography>
                            <Typography variant="body2" className={classes.columnHeaderAttendees}>Attendees</Typography>
                        </Box>
                        <IconButton onClick={() => handleClick("Inspections - Most Recent")} className={classes.inspectionExitButton}>
                            <ClearOutlinedIcon fontSize="small" style={{fill:"white"}}/>
                        </IconButton>
                    </Box>
                    
                    <Box style={{marginTop:'10px'}} className={needsScrolling ? classes.scrollableArea : classes.nonScrollableArea }>
                        {props.recentInspectionData && props.recentInspectionData.map((item, index) => (
                            <Box key={index} className={classes.dateRow}>
                                <Typography variant="body2" style={{fontSize:'0.8rem'}} className={classes.dateColumn}>
                                    {formatDate(item.date)} {item.start_time || ""}
                                </Typography>
                                <Typography variant="body2" style={{fontSize:'0.8rem'}} className={classes.attendeesColumn}>{item.attendees}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            );
        } else {
  
            return (
                <div className={classes.gridContainer}>
                    <div className={classes.fullSection}>
                        <Box className={classes.sectionHeaderContainer}>
                            <Typography style={{marginBottom: '-5px'}} variant="body1" className={classes.sectionHeader}>OFI</Typography>
                            <IconButton onClick={() => handleClick("Inspections - Most Recent")} className={classes.mostRecentInfoButton}>
                                <InfoOutlinedIcon fontSize="small" style={{fill:"white"}}/>
                            </IconButton>
                        </Box>
                        
                        <Box className={classes.sectionContent}>
                            <Typography className={classes.dataLabel} style={{fontSize: '0.95rem', color: 'white', marginTop: '15px'}}>Recent Attendees</Typography>
                            <Typography className={classes.dataValue} style={{color: 'white', fontSize:'0.9rem'}}>Date: {getMostRecentInspection()}</Typography>
                            <Typography className={classes.dataValue} style={{color: 'white'}}>Attendees: {props.recentValue}</Typography>
                        </Box>
                    </div>
                    
                    <div className={classes.fullSection} style={{marginLeft: '7px'}}>
                        <Box className={classes.sectionHeaderContainer}>
                            <Typography variant="body1" className={classes.sectionHeader}>Total</Typography>
                            <IconButton onClick={() => handleClick("Inspections - Total")} className={classes.attendeeCardIconButton}>
                                <InfoOutlinedIcon fontSize="small" style={{fill:"white"}}/>
                            </IconButton>
                        </Box>
                        
                        <Box className={classes.sectionContent}>
                            <Typography className={classes.dataLabel} style={{fontSize: '0.95rem', color: 'white'}}>Total Attendees</Typography>
                            <Typography className={classes.dataValue} style={{color: 'white'}}>{props.value}</Typography>
                        </Box>
                    </div>
                </div>
            );
        }
    };


    if (!isAttendeeCard) {
        return (
            <Card className={props.open === props.name ? classes.clicked : classes.card}>
                <CardHeader 
                    className={classes.cardHeader} 
                    action={
                        <IconButton onClick={() => handleClick(props.name)} className={classes.cardIconButton}>
                            {props.open === props.name ? <ClearOutlinedIcon fontSize="small" style={{fill:"white"}}/> : <InfoOutlinedIcon fontSize="small" style={{fill:"white"}}/>}
                        </IconButton>
                    } 
                />
                <CardContent className={classes.cardContent}>
                    {props.open === props.name ? (
                        <>
                            <Typography variant="subtitle2" style={{color:"white", paddingLeft: "5px"}}>{props.name}</Typography>
                            <Typography variant="subtitle1" style={{color:"white", paddingRight: "30px", paddingLeft: "5px"}}>{infoBox[props.name]}</Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="body1" style={{ color: 'white', fontSize: '1rem' }}>{props.name}</Typography>
                            <Typography variant="h4" style={{ color: 'white', fontSize: '1rem' }}>{props.value}</Typography>
                        </>
                    )}
                </CardContent>
            </Card>
        );
    }


    return (
        <Card className={isAttendeeCardOpen ? classes.clicked : classes.card}>
            <CardContent className={classes.cardContent}>
                {renderAttendeeCard()}
            </CardContent>
        </Card>
    );
}